<template>
  <div>
    <ul class="pagination flex-wrap flex nmr-10 nml-10 list pl-0 mb-justify-center" v-bind="$attrs" :style="{ 'min-width': `${minWidth}px` }">
      <li class="mr-4 tc">
        <DJButton :icon="IconCaretLeft" type="tertiary" size="small" aria-label="Previous page" :disabled="currentPage === 1" @click.prevent="prev" />
      </li>
      <li class="ml-4 mr-4 w-36 h-36 tc flex items-center" v-for="(link, index) in links" :key="index">
        <span v-if="link === '...'" class="wp-100">
          {{ link }}
        </span>
        <DJButton v-else size="small" :type="currentPage == link ? 'primary' : 'tertiary'" @click.prevent="go(link as number)">
          <span class="fw-400">{{ link }}</span>
        </DJButton>
      </li>
      <li class="ml-4 tc">
        <DJButton :icon="IconCaretRight" type="tertiary" size="small" aria-label="Next page" :disabled="currentPage === totalPage" @click="next" />
      </li>
    </ul>
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import DJButton from '~/components/ui/button/Button.vue'
import { IconCaretLeft, IconCaretRight } from '~/components/ui/icon/icons'

const props = defineProps({
  currentPage: {
    required: true,
    type: Number,
    default: 1,
  },
  limit: {
    required: true,
    type: Number,
    default: 5,
  },
  totalPage: {
    required: true,
    type: Number,
    default: 0,
  },
  minWidth: {
    type: Number,
    default: 300,
  },
})
const emits = defineEmits(['action', 'update:currentPage'])
const COUNT_ITEMS_DEFAULT_PAGINATION = 3 // next, prev, 3 dots

const current = ref(1)

const limitPage = computed(() => {
  return props.limit - 1
})

const pages = computed(() => {
  let pages = ref<Array<number>>([])
  for (let i = 1; i <= props.totalPage; i++) {
    pages.value.push(i)
  }

  return pages.value
})

const links = computed(() => {
  let first = ref([1, '...'])
  let last = ref(['...', props.totalPage])
  let range = ref([])

  if (props.totalPage < limitPage.value + COUNT_ITEMS_DEFAULT_PAGINATION) {
    return rangePage(1, props.totalPage)
  } else {
    if (props.currentPage <= limitPage.value) {
      range.value = rangePage(1, limitPage.value + 1)
      return props.currentPage + range.value.length <= props.totalPage ? range.value.concat(last.value as any) : range.value
    } else if (props.currentPage > props.totalPage - limitPage.value) {
      range.value = rangePage(props.totalPage - limitPage.value, props.totalPage)
      return props.currentPage - range.value.length >= 1 ? first.value.concat(range.value) : range.value
    } else {
      range.value = rangePage(props.currentPage - Math.ceil(limitPage.value / 2 - 1), props.currentPage + Math.ceil(limitPage.value / 2 - 1))
      return first.value.concat(range.value).concat(last.value)
    }
  }
})

function rangePage(start: number, end: number) {
  let tempArr = ref<any>([])
  for (let i = start - 1; i < end; i++) {
    if (pages.value[i]) {
      tempArr.value.push(pages.value[i])
    }
  }
  return tempArr.value
}

function prev() {
  if (props.currentPage > 1) {
    current.value = props.currentPage
    current.value--
    emits('update:currentPage', current.value)
    emits('action', current.value)
  }
}

function next() {
  if (props.currentPage < props.totalPage) {
    current.value = props.currentPage
    current.value++
    emits('update:currentPage', current.value)
    emits('action', current.value)
  }
}

function go(page: number) {
  emits('update:currentPage', page)
  emits('action', page)
}
</script>

<style lang="scss" scoped>
.disabled {
  pointer-events: none;
}

// Pagination cells are compact 36px squares (the <li> is w-36 h-36). The small
// DJButton ships text-button padding (0 20px, 40px tall), which overflows the
// cell and wraps unevenly — collapse it to a centred square so single- and
// double-digit page numbers sit centred in the cell.
.pagination :deep(.dj-button) {
  width: 36px;
  min-width: 36px;
  height: 36px;
  padding: 0;
}
</style>
